import { localeManager, platformManager } from '@reactive';
import type { StringId } from '@strings';
import type { AppIcon } from '@ui';
import { expectDefined } from '@utils';
import { commandSpecs, type CommandId, type CommandSpec } from './commandSpecs';

export class Command {
  spec: CommandSpec;
  disabled?: () => boolean;
  handler?: () => void;
  keyUp?: () => void;
  nameOverride?: () => StringId;

  constructor(spec: CommandSpec) {
    this.spec = spec;
  }

  get id(): CommandId {
    return this.spec.id;
  }

  get name(): StringId {
    return this.nameOverride ? this.nameOverride() : this.spec.name;
  }

  get shortName(): StringId | undefined {
    return this.spec.shortName;
  }

  get description(): StringId | undefined {
    return this.spec.description;
  }

  get icon(): AppIcon | undefined {
    return this.spec.icon;
  }

  get keyTrigger(): string | undefined {
    return platformManager.commandKeyTrigger(this);
  }

  get isDisabled(): boolean {
    return this.disabled ? this.disabled() : false;
  }
}

export interface BindCommandOpts {
  keyUpHandler: () => void;
  disabled: () => boolean;
  nameOverride: () => StringId;
}

export class CommandManager {
  commands = new Map<CommandId, Command>();
  private keybindings = new Map<string, CommandId>();
  private lastCommand?: Command;

  constructor() {
    for (const spec of commandSpecs) {
      this.commands.set(spec.id, new Command(spec));
    }
  }

  bind(spec: CommandId, handler: () => void, opts: Partial<BindCommandOpts> = {}) {
    const { keyUpHandler, disabled, nameOverride } = opts;
    const command = expectDefined(this.commands.get(spec));
    command.handler = handler;
    command.keyUp = keyUpHandler;
    command.disabled = disabled;
    command.nameOverride = nameOverride;
    const keyTrigger = command.keyTrigger;
    if (keyTrigger) {
      this.keybindings.set(keyTrigger, command.id);
    }
  }

  getCommand(id: CommandId): Command {
    return expectDefined(this.commands.get(id));
  }

  dispatch(id: CommandId): boolean {
    const command = this.getCommand(id);
    if (command.isDisabled) {
      return false;
    }
    if (command.handler) {
      command.handler();
    }
    this.lastCommand = command;
    return true;
  }

  dispatchKeyUp() {
    if (this.lastCommand?.keyUp) {
      this.lastCommand.keyUp();
    }
    this.lastCommand = undefined;
  }

  logCommandKeys() {
    const keyCommands: [string, string][] = Array.from(this.commands.values()).map(
      command => {
        const keyTrigger = command.keyTrigger;
        return [keyTrigger ?? 'unassigned', localeManager.localizedString(command.name)];
      }
    );
    keyCommands.sort((a, b) => a[0].localeCompare(b[0]));

    // Find the longest key trigger to pad the output
    const maxKeyLength = Math.max(...keyCommands.map(command => command[0].length));

    const logOutput = keyCommands
      .map(command => `${command[0].padEnd(maxKeyLength + 4)}${command[1]}`)
      .join('\n');
    console.log(logOutput);
  }
}
